import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { EditorView } from 'prosemirror-view';
import ReactDOM from 'react-dom';
import { Popover, PopoverBody } from 'reactstrap';

import { insertMergeTag } from '../commands';
import { mergeTagTriggerPluginKey } from '../plugins/mergeTagTrigger';

import MergeTagSelect from './MergeTagSelect';

import './MergeTagDropdown.scss';

const propTypes = {

	/**
	 * Set of merge tags that are available for use.
	 */
	mergeTags: PropTypes.shape({

		organization: PropTypes.shape({

			domain: PropTypes.bool,

		}),

		invitation: PropTypes.shape({

			id: PropTypes.bool,

		}),

	}),

	/**
	 * Flag specifying if mobile view should be rendered.
	 */
	mobileView: PropTypes.bool,

	/**
	 * The prosemirror editor view object.
	 */
	editorView: PropTypes.instanceOf(EditorView).isRequired,

};

const MergeTagDropdown = (props) => {

	const {
		mergeTags,
		mobileView,
		editorView,
	} = props;

	// Get plugin state from current view
	const pluginState = mergeTagTriggerPluginKey.getState(editorView.state);
	const { isMergeTagActive, mergeTagStart, mergeTagEnd, queryText } = pluginState;

	// Get DOM element of merge tag being typed
	const mergeTagDOM = isMergeTagActive && editorView.nodeDOM(mergeTagStart);

	// Enable editor to keep track of reference to react-select component
	const selectRef = useCallback((selectNode) => {

		const tr = editorView.state.tr.setMeta('selectRef', selectNode);

		editorView.dispatch(tr);

	}, [editorView]);

	useEffect(() => {

		const preventPopperScroll = (event) => event.stopImmediatePropagation();

		const selectRefDOM = selectRef?.current;

		// Prevent popper position update on scroll event
		selectRefDOM?.addEventListener('scroll', preventPopperScroll);

		// Clean-up
		return () => selectRefDOM?.removeEventListener('scroll', preventPopperScroll);

	}, [selectRef]);

	const handleChange = (option, { action }) => {

		if (action === 'select-option') {
			// eslint-disable-next-line max-len
			insertMergeTag(option.value, mergeTagStart, mergeTagEnd)(editorView.state, editorView.dispatch);
		}

	};

	if (isMergeTagActive && mergeTagDOM?.parentElement) {

		return (
			<Popover
				className={`tc-pm-merge-tag-dropdown ${mobileView ? 'mobile' : ''}`}
				target={mergeTagDOM?.parentElement}
				placement={mobileView ? 'top-start' : 'bottom-start'}
				fade={false}
				hideArrow
				isOpen>
				<PopoverBody className="p-0">
					<MergeTagSelect
						selectRef={selectRef}
						inputValue={queryText}
						mergeTags={mergeTags}
						onChange={handleChange} />
				</PopoverBody>
			</Popover>
		);

	}

	return <div />;

};

MergeTagDropdown.propTypes = propTypes;

export default MergeTagDropdown;
